'use client'

import React, { PropsWithChildren } from 'react'
import { Badge, Box, Flex, Heading, Link, RatingGroup, Separator, Text } from '@chakra-ui/react'
import { Location, ProductOpeningHoursResponse, Region } from '@leid/api-locations'
import { Manager } from '@leid/api-managers'
import { ProductAvailabilityInfo } from '@leid/api-orders'

import Container from 'app/components/shared/Container'
import { GoBackLink } from 'app/components/shared/nav/GoBackLink'
import { CustomerInfo } from 'app/utils/authentication'
import { ItemApi } from 'appTypes/items'
import { SortingInfo } from 'appTypes/mapper'
import { AddProductToOrderOptions, OrderInfo, OrderItemInfo } from 'appTypes/orders'
import { VideoPreview } from 'components/partials/products/VideoPreview'
import BundleItems from './components/BundleItems'
import ColumnsWrapper from './components/ColumnsWrapper'
import LeftColumn from './components/LeftColumn'
import PriceInfo from './components/PriceInfo'
import ProductImagesSlider from './components/ProductImagesSlider'
import RentalButtonClient from './components/RentalButtonClient'
import RightColumn from './components/RightColumn'
import { useTranslation } from 'react-i18next'
import { useRentProductLogicFormikOptions } from '../RentProduct/RentProductLogic/hooks/useRentProductLogicFormikOptions'
import { useFormik } from 'formik'
import { addProductToOrder, createNewOrder, updateProductInOrder } from 'store/partials/cart/cartSlice'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { getPaymentSelectPageUrl } from 'utils/redirectUrlsBuilders'
import apiClient from 'helpers/clients/apiClient'
import { buildDate, buildUtcDateString } from 'utils/dates'
import { RentProductLoginSubmitHandler } from '../RentProduct/RentProductLogic/interfaces'
import { getFromStorage } from 'utils/storage'
import { STORAGE_KEYS } from 'utils/constants'
import { usePathname, useRouter } from 'next/navigation'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { calculateLengthOfString, isLeidProduct } from 'app/utils/helpers/helpers'
import ProductInformation from './components/ProductInformation'
import Quicklinks from './components/Quicklinks'
import PeriodInfoContentWrapper from './components/PriceInfo/PeriodInfoContentWrapper'
import PeriodInfoDialog from './components/PriceInfo/PeriodInfoDialog'
import ArticleCard from './components/ArticleCard'

type Props = PropsWithChildren & {
  product: ItemApi
  isBundle: boolean
  isProlonging: boolean
  isEditMode: boolean
  order: OrderInfo | undefined
  orderItem: OrderItemInfo | undefined
  customerInfo: CustomerInfo
  productCalendarList: ProductAvailabilityInfo
  productLocations: Location[]
  openingHours: ProductOpeningHoursResponse[]
  rentedFrom: string
  rentedTo: string
  isDeductibles: boolean
  isInsurance: boolean
  locationId: number | undefined
  regionId: number | undefined
  managerId: number | undefined
  amount: number | undefined
  step: number | undefined
  isRerent: boolean
  regions: Region[]
  manager: Manager | undefined
  sortingInfo: SortingInfo | undefined
}

const ProductInfoAndRentalSection = (props: Props): JSX.Element => {
  const {
    product,
    isBundle,
    isProlonging,
    isEditMode,
    order,
    orderItem,
    customerInfo,
    productCalendarList,
    productLocations,
    openingHours,
    rentedFrom,
    rentedTo,
    isDeductibles,
    isInsurance,
    locationId,
    regionId,
    managerId,
    amount,
    step,
    isRerent,
    regions,
    manager,
    sortingInfo,
  } = props

  const router = useRouter()
  const pathname = usePathname()

  const dispatch = useDispatch<AppDispatch>()

  const { t } = useTranslation('common')

  const onSubmit: RentProductLoginSubmitHandler = async (values, { setSubmitting }) => {
    let orderId = getFromStorage(STORAGE_KEYS.orderId)
    if (!orderId) {
      orderId = await dispatch(createNewOrder(values.managerId))
    } else {
      if (values.managerId) {
        // Order exists, if it's not for manager - create a new order instead
        const response = await apiClient.getOrderClean(orderId)
        const order = response.data

        if (!order || order.order.managerId !== values.managerId) {
          orderId = await dispatch(createNewOrder(values.managerId))
        }
      }
    }

    const rentedFrom = buildUtcDateString(values.startDate, values.pickupAt)
    const rentedTo = buildUtcDateString(values.endDate, values.returnAt)
    const rentedToDate = buildDate(values.endDate, values.returnAt)
    const orderItemId = orderItem?.orderItem?.id ?? 0

    if (isProlonging) {
      try {
        const prolongationResponse = await apiClient.createProlongation(orderItemId, {
          prolongTo: rentedToDate,
        })
        if (!prolongationResponse.success || !prolongationResponse.data) {
          if (prolongationResponse.error?.errors.some((s) => s.title === 'Item unavailable')) {
            dispatch(displaySnackbar({ message: t('common:itemUnavailable'), isError: true }))
          } else {
            dispatch(displaySnackbar({ message: t('error'), isError: true }))
          }
          return
        }

        const prolongationInfo = prolongationResponse.data

        if (prolongationInfo.confirmed) {
          router.push(`/payment/success?isProlonging=true&order_item_id=${orderItemId}`)
        } else {
          const prolongationOrderId = prolongationInfo.prolongation.orderId
          const orderItemId = prolongationInfo.prolongation.id
          const purchaseAs = customerInfo.organization ? 'organization' : 'person'

          router.push(getPaymentSelectPageUrl(prolongationOrderId, orderItemId, purchaseAs))
        }
      } catch {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
      }
    } else {
      const options: AddProductToOrderOptions = {
        orderId,
        itemId: product.id,
        rentedFrom,
        rentedTo,
        resignFromCleaning: true,
        resignFromInsurance: !values.includeInsurance,
        resignFromDeductibles: !values.includeDeductibles,
        complementaryItems: values.complementaryItemsQuantity,
        amount: values.amount,
        locationId: values.selectedLocationId ?? 0,
        regionId: values.selectedRegionId,
        pathname: pathname ?? '',
      }

      if (isEditMode) {
        dispatch(updateProductInOrder({ ...options, orderItemId })).finally(() => {
          setSubmitting(false)
        })
      } else {
        dispatch(addProductToOrder(options)).finally(() => {
          setSubmitting(false)
        })
      }
    }
  }

  const formikOptions = useRentProductLogicFormikOptions({
    onSubmit,
    amount,
    isDeductibles,
    isInsurance,
    isProlonging,
    locationId,
    managerId,
    regionId,
    rentedFrom,
    rentedTo,
  })
  const formikConfig = useFormik(formikOptions)
  const { values } = formikConfig

  const selectedLocation = values.selectedLocationId
    ? productLocations.find((s) => s.id === values.selectedLocationId)
    : undefined

  return (
    <Container>
      <GoBackLink />
      <Box style={{ marginBottom: '20px' }}>
        {isBundle ? (
          <Text marginBottom="6px" fontWeight="nonrmal" color="updatedLeid.green">
            {t('productBundle')}
          </Text>
        ) : (
          <Text marginBottom="6px" fontWeight="normal" color="updatedLeid.green" className="gtm-product-category">
            {product.categories.map(
              (category, index) => `${category.name} ${index !== product.categories.length - 1 ? '/' : ''} `,
            )}
          </Text>
        )}
        <Heading
          as="h2"
          fontSize={{ base: 'md', lg: 'xl' }}
          fontWeight="semibold"
          marginBottom="12px"
          className="gtm-product-name"
          wordBreak="break-word"
          lineHeight={{ base: 1.33, md: 1.2 }}
        >
          {product.name}
        </Heading>

        <RatingGroup.Root count={5} value={4.5} size="xs" colorPalette="orange" readOnly>
          <RatingGroup.HiddenInput />
          <RatingGroup.Control />
        </RatingGroup.Root>
        <Text fontSize="sm" fontWeight="normal" color="gray.a500" marginBottom="12px">
          {t('averageRating')}
        </Text>

        {/* <Separator /> */}
      </Box>
      {(orderItem || !isProlonging) && (
        <Box marginBottom="60px">
          <ColumnsWrapper>
            <LeftColumn>
              {!isBundle && <ProductImagesSlider images={product?.products[0] && product?.products[0].images} />}
              <Box marginTop={10}>
                <Heading
                  as="h2"
                  fontSize={{ base: 'md', lg: 'lg' }}
                  fontWeight="semibold"
                  marginBottom="12px"
                  className="gtm-product-name"
                  wordBreak="break-word"
                  lineHeight={{ base: 1.33, md: 1.2 }}
                >
                  {product.name}
                </Heading>
                <Box display="flex" flexDirection="row" gap={2} alignItems="start" maxWidth="100%">
                  <Text fontSize="sm" fontWeight="normal" color="gray.a500" lineClamp={3} width="100%">
                    {product.description}
                  </Text>
                  {calculateLengthOfString(product.description ?? '') > 10 && (
                    <Link
                      color="updatedLeid.green"
                      fontSize="sm"
                      fontWeight="semibold"
                      display="inline-block"
                      width="105px"
                      href={`#product-description`}
                    >
                      {t('readMore')}
                    </Link>
                  )}
                </Box>
              </Box>
              <Text fontSize="sm" fontWeight="bold" color="gray.a500" marginTop="20px">
                {t('price')}:
              </Text>

              <Flex direction="column">
                <Box marginTop={{ base: 0, lg: '0px' }}>
                  <PriceInfo
                    showExtraInfo
                    product={product}
                    location={selectedLocation}
                    customerInfo={customerInfo}
                    values={values}
                  />
                </Box>

                <Quicklinks
                  masterArticle={product.masterArticle ?? undefined}
                  relatedArticle={product.relatedArticles.length !== 0 ? product.relatedArticles[0] : undefined}
                />
              </Flex>
              <Box
                display={{ lg: 'none' }}
                marginTop="24px"
                marginBottom={0}
                position="fixed"
                bottom="10px"
                left="10px"
                right="10px"
                backgroundColor="white"
                zIndex="docked"
                padding={4}
                width="calc(100% - 20px)"
                borderRadius="10px"
                boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.1)"
              >
                <Text fontSize="sm" lineHeight="1.5" fontWeight="semibold">
                  {t('price')}:
                </Text>
                <Text fontSize="md" lineHeight="1.5" fontWeight="semibold">
                  {t('from')} {product.pricingInfo?.[0]?.minimalPrice} {t('nok')}
                </Text>
                {isLeidProduct(product) && (
                  <Badge colorPalette="green" size="md" padding={2} marginBottom={2}>
                    15% {t('leidSmart.cashback')}
                  </Badge>
                )}

                {!customerInfo.customer && (
                  <Badge colorPalette="orange" size="md" padding={2} marginBottom={2}>
                    {t('newUser50Discount')}
                  </Badge>
                )}

                <RentalButtonClient>{t(isEditMode || isProlonging ? 'update' : 'checkPrice')}</RentalButtonClient>
              </Box>
            </LeftColumn>

            <Box maxWidth="60%">
              <Box display={{ base: 'none', lg: 'block' }}>
                <PeriodInfoDialog product={product} location={selectedLocation} />
              </Box>
              <RightColumn
                product={product}
                isEditMode={isEditMode}
                isProlonging={isProlonging}
                customerInfo={customerInfo}
                order={order}
                orderItem={orderItem}
                productLocations={productLocations}
                productCalendarList={productCalendarList}
                openingHours={openingHours}
                rentedFrom={rentedFrom}
                isRerent={isRerent}
                step={step}
                regions={regions}
                manager={manager}
                sortingInfo={sortingInfo}
                formikOptions={formikOptions}
                formikConfig={formikConfig}
              />
            </Box>
          </ColumnsWrapper>
        </Box>
      )}

      <Box>
        <Separator marginBottom="40px" id="product-description" />
        <Heading
          as="h2"
          fontSize={{ base: 'md', lg: 'xl' }}
          fontWeight="semibold"
          marginBottom="12px"
          className="gtm-product-name"
          wordBreak="break-word"
          lineHeight={{ base: 1.33, md: 1.2 }}
        >
          {product.name}
        </Heading>

        <RatingGroup.Root count={5} value={4.5} size="xs" colorPalette="orange" readOnly>
          <RatingGroup.HiddenInput />
          <RatingGroup.Control />
        </RatingGroup.Root>
        <Text fontSize="sm" fontWeight="normal" color="gray.a500" marginBottom="12px">
          {t('averageRating')}
        </Text>
        <Text fontSize="sm" fontWeight="normal" color="gray.a500" width="100%">
          {product.description}
        </Text>
        <Box marginBottom={{ base: '20px', lg: '12px' }} marginTop={{ base: 0, lg: '12px' }}>
          {isBundle && <BundleItems product={product} />}
          <Box wordBreak="break-word" marginBottom="12px">
            {product?.products[0] && product?.products[0].videoUrl && (
              <VideoPreview
                videoThumbnailUrl={product?.products[0].videoThumbnailUrl}
                videoLength={product?.products[0].videoLength}
                videoTitle={product?.products[0].videoTitle}
                videoUrl={product?.products[0].videoUrl}
              />
            )}
          </Box>
        </Box>
        <Box marginBottom="40px" id="technical-details" scrollMarginTop="200px">
          <ProductInformation product={product} isBundle={isBundle} />
        </Box>
        <Separator marginBottom="40px" />
        <Box>
          <PeriodInfoContentWrapper product={product} location={selectedLocation} />
        </Box>
      </Box>
      <Separator marginBottom="40px" />
      <Flex direction={{ base: 'column', md: 'row' }} marginBottom="40px" gap={20}>
        {product.masterArticle && (
          <Box id="how-to-pick-up-the-product" scrollMarginTop="200px" maxWidth="calc(50% - 10px)">
            <Heading
              fontWeight="semibold"
              as="h5"
              marginTop="40px"
              marginBottom="30px"
              maxWidth="80%"
              lineHeight="normal"
            >
              {' '}
              {t('howToPickup')}
            </Heading>
            <ArticleCard article={product.masterArticle} />
          </Box>
        )}
        {product.relatedArticles.length !== 0 && product.relatedArticles && (
          <Box id="product-instructions" scrollMarginTop="200px" maxWidth="calc(50% - 10px)">
            <Heading
              fontWeight="semibold"
              as="h5"
              marginTop="40px"
              marginBottom="30px"
              maxWidth="80%"
              lineHeight="normal"
            >
              {t('productInstructions')}
            </Heading>
            <ArticleCard article={product.relatedArticles[0]} />
          </Box>
        )}
      </Flex>
    </Container>
  )
}
export default ProductInfoAndRentalSection
